/*
 * Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { getDefaultSchemaFieldImpl } from "cms/fields/get-default-field-impl";
import { initializeField } from "cms/fields/transformers/initialize-field";
import createCachedSelector from "re-reselect";
import { createDeepEqualSelector } from "data/store/create-deep-equal-selector";


/**
 * @param {RootState} state
 */
export function selectCurrentModel (state) {
    return state.model.current;
}

/**
 * @param {RootState} state
 */
export function selectModelLoading (state) {
    return state.model.loading;
}

/**
 * @param {RootState} state
 */
export function selectCurrentModelType (state) {
    return state.model.current?.type;
}

/**
 * @param {RootState} state
 */
export function selectHasCurrentModel (state) {
    return !!state.model.current;
}

/**
 * @param {RootState} state
 * @returns {any[]}
 */
export function selectCurrentFields (state) {
    return state.model.current?.fields || [];
}

export const selectFieldByPosition = createCachedSelector(
    // Performance: uses re-reselect to cache the work done by the selector when the input is the same
    // we have to duplicate selectCurrentModelSchema otherwise we get a circular reference
    (state) => state.schemas.all || [],
    selectCurrentModelType,
    selectCurrentFields,
    /**
     * 
     * @param {RootState} state 
     * @param {number} position 
     * @returns 
     */
    (state, position) => position,
    (schemas, modelType, fields, position) => {

        if (!fields || fields.length === 0) {
            return null;
        }
        
        const schema = schemas.find((element) => {
            return element.type === modelType;
        });

        const field = fields[position];
        const schemaField = schema.fields.find((sField) => {
            return sField?.name === field?.name;
        });
        if (!schemaField) {
            return null;
        }
        const defaultImpl = getDefaultSchemaFieldImpl(schema.settings, schemaField?.type);
        const initializedField = initializeField(schemaField, field, defaultImpl);

        return initializedField;
    }
)(
    {
        keySelector (state, position) {
            const guid = state.model.current?.guid;
            const key = [guid, "field", position].join(":");

            return key;
        },
        selectorCreator: createDeepEqualSelector
    }
);

export const selectCurrentFieldInfo = createDeepEqualSelector(
    selectCurrentFields,
    /**
     * @returns {Array<{type: string, name: string}>}
     */
    (fields) => fields.map((field) => {
        return {
            type: field.type,
            name: field.name,
        };
    })
);


/**
 * @typedef {import("./model.state")["modelState"]} ModelState
 */

/**
 * @typedef {object} RootState
 * @property {ModelState} model
 */
